import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import React from 'react';

// ── Animation variants ──────────────────────────────────
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.18 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 0.68, 0, 1.2] },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.82 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: 'easeOut' },
  },
};

const floatingVariants = {
  animate: {
    y: [0, -10, 0],
    transition: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' },
  },
};

const floatingVariantsSlow = {
  animate: {
    y: [0, -6, 0],
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.8 },
  },
};

// ── Component ───────────────────────────────────────────
const HeroSection = ({ title, subtitle, actions, stats, images, className }) => {
  return (
    <section className={cn('w-full overflow-hidden bg-background py-12 sm:py-20', className)}>
      <div className="container mx-auto px-4 grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-10">

        {/* ── Left: Text ── */}
        <motion.div
          className="flex flex-col items-center text-center lg:items-start lg:text-left"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <span
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-5"
              style={{ backgroundColor: '#43a047', color: '#fff' }}
            >
              B2B Lead Generation
            </span>
          </motion.div>

          <motion.h1
            className="text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-[3.2rem] leading-tight"
            variants={itemVariants}
          >
            {title}
          </motion.h1>

          <motion.p
            className="mt-5 max-w-md text-base text-muted-foreground leading-relaxed"
            variants={itemVariants}
          >
            {subtitle}
          </motion.p>

          <motion.div
            className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start"
            variants={itemVariants}
          >
            {actions.map((action, index) => (
              <Button
                key={index}
                onClick={action.onClick}
                variant={action.variant || 'default'}
                size="lg"
                className={cn('rounded-full font-semibold px-8', action.className)}
              >
                {action.text}
              </Button>
            ))}
          </motion.div>

          <motion.div
            className="mt-10 flex flex-wrap justify-center gap-7 lg:justify-start"
            variants={itemVariants}
          >
            {stats.map((stat, index) => (
              <div key={index} className="flex items-center gap-3">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-full"
                  style={{ backgroundColor: '#e8f5e9' }}
                >
                  {stat.icon}
                </div>
                <div>
                  <p className="text-xl font-extrabold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* ── Right: Image Collage ── */}
        <motion.div
          className="relative h-[380px] w-full sm:h-[480px]"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Decorative blobs */}
          <motion.div
            className="absolute -top-5 left-1/4 h-16 w-16 rounded-full"
            style={{ backgroundColor: 'rgba(67,160,71,0.18)' }}
            variants={floatingVariants}
            animate="animate"
          />
          <motion.div
            className="absolute bottom-4 right-1/4 h-10 w-10 rounded-xl"
            style={{ backgroundColor: 'rgba(67,160,71,0.14)' }}
            variants={floatingVariantsSlow}
            animate="animate"
          />
          <motion.div
            className="absolute bottom-1/3 left-4 h-7 w-7 rounded-full"
            style={{ backgroundColor: 'rgba(67,160,71,0.22)' }}
            variants={floatingVariants}
            animate="animate"
          />

          {/* Top-center image — largest */}
          <motion.div
            className="absolute left-1/2 top-0 h-52 w-52 -translate-x-1/2 rounded-2xl bg-muted p-1.5 shadow-xl sm:h-64 sm:w-64"
            variants={imageVariants}
          >
            <img
              src={images[0]}
              alt="B2B business team"
              className="h-full w-full rounded-xl object-cover"
            />
          </motion.div>

          {/* Right image */}
          <motion.div
            className="absolute right-0 top-1/3 h-40 w-40 rounded-2xl bg-muted p-1.5 shadow-xl sm:h-52 sm:w-52"
            variants={imageVariants}
          >
            <img
              src={images[1]}
              alt="Data analytics"
              className="h-full w-full rounded-xl object-cover"
            />
          </motion.div>

          {/* Bottom-left image */}
          <motion.div
            className="absolute bottom-0 left-0 h-32 w-32 rounded-2xl bg-muted p-1.5 shadow-xl sm:h-44 sm:w-44"
            variants={imageVariants}
          >
            <img
              src={images[2]}
              alt="Lead generation strategy"
              className="h-full w-full rounded-xl object-cover"
            />
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
};

export default HeroSection;
